import React, { useState, useRef, useCallback } from 'react';

interface VideoCardProps {
  src: string;
  poster: string;
  title: string;
  subtitle?: string;
  aspectRatio?: string;
}

export default function VideoCard({ src, poster, title, subtitle, aspectRatio = 'video' }: VideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    videoRef.current?.play();
  }, []);

  const aspectClass = aspectRatio === 'square' ? 'aspect-square' : 'aspect-video';

  return (
    <div className="group relative rounded-lg overflow-hidden border border-background-200/60 bg-background-100">
      <div className={`relative ${aspectClass} min-h-[200px] md:min-h-[280px]`}>
        {!isPlaying ? (
          <>
            <img
              src={poster}
              alt={title}
              className="w-full h-full object-cover select-none"
              draggable="false"
            />
            {/* Anti-download overlay */}
            <div
              className="absolute inset-0 z-10"
              onContextMenu={(e) => e.preventDefault()}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all duration-300" />
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center cursor-pointer z-20"
              aria-label={`Reproducir video: ${title}`}
            >
              <div className="w-14 h-14 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-white/90 hover:bg-white group-hover:scale-110 transition-all duration-300 shadow-lg">
                <i className="ri-play-fill text-foreground-950 text-xl md:text-3xl ml-1" />
              </div>
            </button>
            {subtitle && (
              <div className="absolute bottom-3 left-3 right-3 z-20">
                <div className="inline-block px-2.5 py-1 rounded-md bg-black/60 text-white text-[10px] font-medium backdrop-blur-sm">
                  {subtitle}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <video
              ref={videoRef}
              src={src}
              controls
              autoPlay
              className="w-full h-full object-cover"
              controlsList="nodownload"
              onEnded={() => setIsPlaying(false)}
              playsInline
            />
            {/* Anti-download overlay on video controls area */}
            <div
              className="absolute inset-0 z-10 pointer-events-none"
              onContextMenu={(e) => e.preventDefault()}
              aria-hidden="true"
            />
          </>
        )}
      </div>
      <div className="p-3 md:p-4 bg-background-50">
        <h4 className="font-heading text-sm font-bold text-foreground-950">{title}</h4>
      </div>
    </div>
  );
}